import React from 'react';

/**
 * Renders plain text with any URLs turned into clickable links.
 *
 * Form sections often contain reference chapters with long documentation links.
 * Those links must both be clickable and stay inside their card, so the wrapper
 * allows breaking inside the URL — otherwise a single long link forces the whole
 * card to overflow horizontally.
 */

// Matches http(s):// and bare www. links. The character class stops at
// whitespace and at brackets/quotes so a link inside parentheses doesn't
// swallow the closing bracket.
const URL_PATTERN = /((?:https?:\/\/|www\.)[^\s<>()[\]{}"'`]+)/gi;

// Sentence punctuation directly after a URL belongs to the sentence, not the link.
const TRAILING_PUNCTUATION = /[.,;:!?]+$/;

const wrapStyle = {
  // `anywhere` lets the browser break mid-URL only when it would otherwise
  // overflow, so normal prose still wraps on word boundaries.
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
};

const linkStyle = {
  color: 'var(--ids-color-primary, #0065bd)',
  textDecoration: 'underline',
};

export const buildLinkifiedNodes = (text) => {
  if (text === null || text === undefined) return [];

  const value = String(text);
  const nodes = [];
  let lastIndex = 0;
  let key = 0;

  // matchAll is used rather than split + test: a /g regex keeps its lastIndex
  // between test() calls, which makes split-based linkifying order-dependent.
  for (const match of value.matchAll(URL_PATTERN)) {
    const raw = match[0];
    const start = match.index;

    const trailing = raw.match(TRAILING_PUNCTUATION)?.[0] ?? '';
    const url = trailing ? raw.slice(0, -trailing.length) : raw;

    if (!url) continue;

    if (start > lastIndex) {
      nodes.push(value.slice(lastIndex, start));
    }

    nodes.push(
      <a
        key={`link-${key++}`}
        href={url.toLowerCase().startsWith('www.') ? `https://${url}` : url}
        target="_blank"
        rel="noopener noreferrer"
        style={linkStyle}
      >
        {url}
      </a>
    );

    if (trailing) nodes.push(trailing);
    lastIndex = start + raw.length;
  }

  if (lastIndex < value.length) {
    nodes.push(value.slice(lastIndex));
  }

  return nodes;
};

const LinkifiedText = ({ children, as: Tag = 'p', className, style }) => {
  if (children === null || children === undefined || children === '') return null;

  return (
    <Tag className={className} style={{ ...wrapStyle, ...style }}>
      {buildLinkifiedNodes(children)}
    </Tag>
  );
};

export default LinkifiedText;
